import { CheckCircle, AlertCircle, XCircle, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

export const MenuReadinessPanel = ({ categoryStatus }) => {
  if (!categoryStatus?.length) return null;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-semibold text-lg text-foreground flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Menu Readiness
          </h2>
          <p className="text-sm text-muted-foreground">
            All categories must be ready before publishing
          </p>
        </div>
        {categoryStatus.every(cat => cat.status === 'ready') ? (
          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircle className="h-3 w-3 mr-1" />
            Ready to Publish
          </Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            <AlertCircle className="h-3 w-3 mr-1" />
            Action Required
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {categoryStatus.map(cat => {
          const readyPercent = cat.total_items > 0
            ? Math.round((Math.min(cat.items_with_images, cat.items_with_descriptions, cat.items_with_prices) / cat.total_items) * 100)
            : 0;

          return (
            <Card
              key={cat.menu_type}
              className={`transition-all ${
                cat.status === 'ready' ? 'border-green-300 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20' :
                cat.status === 'incomplete' ? 'border-amber-300 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20' :
                'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20'
              }`}
              data-testid={`category-status-${cat.menu_type}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {cat.status === 'ready' ? (
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                    ) : cat.status === 'incomplete' ? (
                      <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                    )}
                    <h3 className="font-semibold text-sm text-foreground">{cat.label}</h3>
                  </div>
                  <span className="text-xs font-medium text-muted-foreground">
                    {cat.total_items} items
                  </span>
                </div>

                {cat.total_items > 0 && (
                  <div className="mb-2">
                    <Progress
                      value={readyPercent}
                      className={`h-1.5 ${
                        cat.status === 'ready' ? '[&>div]:bg-green-500' :
                        cat.status === 'incomplete' ? '[&>div]:bg-amber-500' : '[&>div]:bg-red-500'
                      }`}
                    />
                  </div>
                )}

                <p className="text-xs text-muted-foreground">{cat.message}</p>

                {cat.status === 'incomplete' && cat.total_items > 0 && (
                  <div className="mt-2 pt-2 border-t border-border space-y-1">
                    {cat.items_with_images < cat.total_items && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-amber-500" />
                        {cat.total_items - cat.items_with_images} missing images
                      </p>
                    )}
                    {cat.items_with_descriptions < cat.total_items && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-amber-500" />
                        {cat.total_items - cat.items_with_descriptions} missing descriptions
                      </p>
                    )}
                    {cat.items_with_prices < cat.total_items && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-amber-500" />
                        {cat.total_items - cat.items_with_prices} missing prices
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {!categoryStatus.every(cat => cat.status === 'ready' || cat.status === 'empty') && (
        <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Menu cannot be published yet
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                Complete all items with images, descriptions, and prices before scheduling the weekly menu.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuReadinessPanel;
